import React from 'react'
import { graphql } from 'react-apollo'
import { withRouter } from 'react-router-dom'

import { ConfirmationModal, Spinner } from '@acorns/web-components'
import { withAnalyticsClient } from '@acorns/web-utils'
import {
  branch,
  compose,
  renderComponent,
  withHandlers,
  withProps,
  withPropsOnChange,
  withState,
} from 'recompose'
import styled from 'styled-components'

import { ModalAction, ModalActionTypes } from 'components/ConfirmationModal'
import FullscreenTakeover from 'components/FullscreenTakeover'
import { LaterWithdrawalReason } from 'generated/types'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { routes } from 'router/routes'

import { CloseAccountWrapper } from '../closeAccountWrapper'
import LoadingScreen from '../closureLoading'
import stopRecurringInvestmentMutation from '../mutations/cancelInvestmentAccountRecurringInvestment.gql'
import mutation from '../mutations/mutation.gql'
import laterLiquidationMutation from '../mutations/withdrawBalance.gql'
import laterBalanceQuery from '../queries/laterQuery.gql'
import userQuery from '../queries/userQuery.gql'
import { ConfirmationScreen } from './confirmation-screen'
import { RequestScreen } from './request-screen'
import {
  CloseAccountLaterNextButton,
  CloseLaterBalanceScreenButtonTapped,
  CloseLaterBalanceScreenViewed,
  CloseLaterConfirmationModalTapped,
  CloseLaterConfirmationModalTappedNevermind,
  CloseLaterConfirmationSuccessButtonTapped,
  CloseLaterConfirmationSuccessScreenViewed,
} from './segment'
import { WithdrawWithBalanceConfirmation } from './with-balance-request-screen'

const Loader = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
`

type HandlerProps = {
  handleCloseAccountSelection?: () => void
  handleCancel?: () => void
  handleCloseAccountConfirmation?: () => void
  handleDoneClick?: () => void
  handleDone?: () => void
  handleExit?: () => void
  handleWithdrawLater?: () => void
  handleWithdrawError?: () => void
}

type GqlProps = {
  laterBalance?: any
  laterAccountId?: string
  recurringInvestments?: {}
}

type Props = {
  closureItems: string[]
  liquidationData: {
    amount: number
    investmentAccountId: number
    stateTaxes: number
    taxYear: number
    reason: LaterWithdrawalReason
  }
  fullWithdrawItem: number
  isWebView: boolean
  modalActions: ModalAction[]
  modalBody: string
  modalHeader: string
  showCloseAccountModal: boolean
  showConfirmRequestScreen: boolean
  showConfirmWithBalance: boolean
  showLoadingScreen: boolean
  showSuccessScreen: boolean
  userData: object
  setShowCloseAccountModal: (showCloseAccountModal: boolean) => void
  setShowConfirmRequestScreen: (showConfirmRequestScreen: boolean) => void
  setShowConfirmWithBalance: (showConfirmWithBalance: boolean) => void
  setShowSuccessScreen: (showSuccessScreen: boolean) => void
  setShowLoadingScreen: (showLoadingScreen: boolean) => void
}

export type MergedProps = HandlerProps & GqlProps & Props

const getModalProps = (props: MergedProps) => {
  const modalActions: ModalAction[] = [
    {
      handler: () =>
        props.setShowCloseAccountModal(!props.showCloseAccountModal),
      label: 'Never mind',
      type: ModalActionTypes.secondary,
      eventFields: CloseLaterConfirmationModalTappedNevermind,
    },
    {
      handler: props.handleCloseAccountConfirmation,
      label: 'Close account',
      type: ModalActionTypes.danger,
      eventFields: CloseLaterConfirmationModalTapped,
    },
  ]
  const modalBody = 'This will close your Later account'
  const modalHeader = 'Are you sure?'
  return {
    modalActions,
    modalBody,
    modalHeader,
  }
}

const getErrorModalProps = (props: MergedProps) => {
  const modalHeader = 'Contact support'
  const modalBody =
    'An error occurred. Please contact support to continue closing your account(s).'
  const modalActions = [
    {
      label: 'OK',
      type: ModalActionTypes.secondary,
      handler: () => props.handleWithdrawError,
    },
  ]
  return {
    modalActions,
    modalBody,
    modalHeader,
  }
}

const enhance = compose<MergedProps, {}>(
  withRouter,
  withAnalyticsClient,
  graphql(laterBalanceQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
  }),
  graphql(userQuery, { name: 'userInfo' }),
  graphql(mutation, {
    name: 'requestCloseAccountMutation',
  }),
  graphql(stopRecurringInvestmentMutation, {
    name: 'stopRecurringInvestmentRequest',
  }),
  graphql(laterLiquidationMutation, {
    name: 'laterTotalLiquidationlMutation',
  }),
  withState('showCloseAccountModal', 'setShowCloseAccountModal', false),
  withState('showLoadingScreen', 'setShowLoadingScreen', false),
  withState('showSuccessScreen', 'setShowSuccessScreen', false),
  withState(
    'showConfirmRequestScreen',
    'setShowConfirmationRequestScreen',
    false,
  ),
  withState('showConfirmWithBalance', 'setShowConfirmWithBalance', false),
  withState('showContactSupportModal', 'setShowContactSupportModal', false),
  withProps(() => ({
    isWebView: window.location.pathname.startsWith('/wv/'),
  })),

  withProps(({ data, location, analytics, CancelSubscriptionScreenViewed }) => {
    if (!data.loading) {
      analytics.track('Screen Viewed', CancelSubscriptionScreenViewed)

      const laterBalance = data?.laterAccounts?.[0]?.amount || false

      const recurringInvestments = data?.laterAccounts?.[0]?.recurringSettings

      const laterAccountId = data?.laterAccounts?.[0]?.accountId || undefined

      const withdrawComplete =
        location?.state?.withdrawAcknowledgementComplete || false

      const isCancelSubscriptionFlow = location?.state?.isCancelSubscriptionFlow
      const isDowngradeSubscriptionFlow =
        location?.state?.isDowngradeSubscriptionFlow

      const liquidationData = withdrawComplete
        ? location?.state?.liquidationData
        : null

      const loading =
        data?.allPastItems?.edges === undefined ||
        data.laterAccounts?.[0]?.recurringSettings === undefined ||
        withdrawComplete === undefined

      const hasSpendAccount = location?.state?.hasSpendAccount
      const closureFormComment = location?.state?.closureFormComment
      const closureFormReason = location?.state?.closureFormReason
      return {
        hasSpendAccount,
        isCancelSubscriptionFlow,
        isDowngradeSubscriptionFlow,
        laterBalance,
        recurringInvestments,
        laterAccountId,
        loading,
        withdrawComplete,
        liquidationData,
        closureFormComment,
        closureFormReason,
      }
    }
    return {
      loading: true,
    }
  }),
  withProps(({ userInfo }) => {
    const userData = userInfo?.user || undefined

    return {
      userData,
    }
  }),
  withProps(({ recurringInvestments, withdrawComplete }) => {
    const closureItems = []
    if (recurringInvestments?.amount !== null && withdrawComplete) {
      closureItems.push(
        'Turning off transfers',
        'Withdrawing balance',
        'Requesting closure',
      )
    } else if (recurringInvestments?.amount === null && withdrawComplete) {
      closureItems.push('Withdrawing balance', 'Requesting closure')
    } else if (recurringInvestments?.amount !== null && !withdrawComplete) {
      closureItems.push('Turning off transfers')
    }

    return {
      closureItems,
    }
  }),

  withPropsOnChange(
    ['history', 'withdrawComplete', 'data'],
    ({
      setShowConfirmationRequestScreen,
      setShowConfirmWithBalance,
      withdrawComplete,
      analytics,
    }) => {
      if (withdrawComplete) {
        setShowConfirmationRequestScreen(false)
        setShowConfirmWithBalance(true)
        analytics.track('Screen Viewed', CloseLaterBalanceScreenViewed)
      } else {
        setShowConfirmationRequestScreen(true)
      }
    },
  ),
  withHandlers({
    handleCancel:
      ({ history, isWebView }) =>
      () => {
        if (isWebView) {
          history.push('/wv/my-subscription')
        } else {
          history.push('/settings/my-subscription')
        }
      },
    handleCloseAccountSelection:
      ({
        analytics,
        hasSpendAccount,
        isCancelSubscriptionFlow,
        isWebView,
        history,
        location,
        setShowCloseAccountModal,
        closureFormComment,
        closureFormReason,
      }) =>
      () => {
        analytics.track('Button Tapped', CloseLaterBalanceScreenButtonTapped)
        const url = isWebView
          ? '/wv/cancel-subscription'
          : '/cancel-subscription'
        isCancelSubscriptionFlow
          ? history.push(url, {
              isCancelSubscriptionFlow: true,
              referrer: location.pathname,
              shouldShowClosureConfirmationScreen: true,
              showNoBalanceScreen: true,
              hasLaterAccount: true,
              hasSpendAccount,
              closureFormReason,
              closureFormComment,
            })
          : setShowCloseAccountModal(true)
      },
    handleCloseAccountConfirmation:
      ({
        analytics,
        laterAccountId,
        liquidationData,
        laterTotalLiquidationlMutation,
        requestCloseAccountMutation,
        recurringInvestments,
        setShowLoadingScreen,
        setShowConfirmationRequestScreen,
        setShowSuccessScreen,
        stopRecurringInvestmentRequest,
        setShowCloseAccountModal,
        setShowConfirmWithBalance,
        showConfirmWithBalance,
        userData,
        setShowContactSupportModal,
      }) =>
      () => {
        setShowCloseAccountModal(false)
        const input = {
          category: 'Close My Account',
          uuid: userData.uuid,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          message: 'Requesting closure of my Later account',
          phone: userData.phoneNumber,
          subcategory: 'Close My Acorns Later Account',
          tags: [
            'automatic_close_my_account',
            'automatic_close_my_acorns_later_account',
          ],
        }
        analytics.track('Button Tapped', CloseLaterConfirmationModalTapped)
        if (liquidationData) {
          laterTotalLiquidationlMutation({
            variables: {
              investmentAccountId: liquidationData.investmentAccountId,
              federalTaxes: liquidationData.federalTaxes,
              stateTaxes: liquidationData.stateTaxes,
              taxYear: liquidationData.taxYear,
              reason: liquidationData.reason,
            },
          })
            .then(() => {
              setShowLoadingScreen(true)
              showConfirmWithBalance
                ? setShowConfirmWithBalance(false)
                : setShowConfirmationRequestScreen(false)
              if (recurringInvestments.amount) {
                stopRecurringInvestmentRequest({
                  variables: { investmentAccountId: laterAccountId },
                })
              }

              requestCloseAccountMutation({
                variables: { input },
              }).then(() => {
                sessionStorage.removeItem('laterCloseAccountWithdrawFlow')
                setShowLoadingScreen(false)
                setShowSuccessScreen(true)
                analytics.track('Screen Viewed', {
                  ...CloseLaterConfirmationSuccessScreenViewed,
                })
              })
            })
            .catch(() => {
              setShowContactSupportModal(true)
            })
        } else {
          setShowLoadingScreen(true)
          showConfirmWithBalance
            ? setShowConfirmWithBalance(false)
            : setShowConfirmationRequestScreen(false)
          if (recurringInvestments.amount) {
            stopRecurringInvestmentRequest({
              variables: { investmentAccountId: laterAccountId },
            })
          }

          requestCloseAccountMutation({
            variables: { input },
          }).then(() => {
            sessionStorage.removeItem('laterCloseAccountWithdrawFlow')
            setShowLoadingScreen(false)
            setShowSuccessScreen(true)
            analytics.track('Screen Viewed', {
              ...CloseLaterConfirmationSuccessScreenViewed,
            })
          })
        }
      },
    handleWithdrawError:
      ({ history, isWebView }) =>
      () => {
        const url = isWebView
          ? '/wv/my-subscription'
          : '/settings/my-subscription'
        history.push(url)
      },
    handleDoneClick:
      ({ isWebView, history, location, analytics }) =>
      () => {
        analytics.track(
          'Button Tapped',
          CloseLaterConfirmationSuccessButtonTapped,
        )
        if (isWebView) {
          window.location.assign('acorns://exit?navigate_to=present')
        } else {
          history.push('/present', {
            referrer: location.pathname,
          })
        }
      },
    handleWithdrawLater:
      ({
        analytics,
        isCancelSubscriptionFlow,
        isDowngradeSubscriptionFlow,
        isWebView,
        history,
        location,
      }) =>
      () => {
        const stateProps = {
          referrer: location.pathname,
          isCancelSubscriptionFlow,
          isDowngradeSubscriptionFlow,
        }
        analytics.track('Button Tapped', CloseAccountLaterNextButton)
        if (isWebView) {
          history.push('/wv/invest/later/withdraw', stateProps)
        } else {
          history.push(routes.later.withdraw, stateProps)
        }
      },
  }),
  withProps<any, any>((props) => ({
    confirmationModalProps: getModalProps(props),
    contactSupportModalProps: getErrorModalProps(props),
  })),
  branch(
    ({ loading }) => loading,
    renderComponent(() => (
      <Loader>
        <Spinner />
      </Loader>
    )),
  ),
)

const Page: React.FC<MergedProps> = ({
  isWebView,
  handleCancel,
  handleCloseAccountSelection,
  handleDoneClick,
  laterBalance,
  confirmationModalProps,
  setShowCloseAccountModal,
  showCloseAccountModal,
  showConfirmRequestScreen,
  showLoadingScreen,
  showSuccessScreen,
  closureItems,
  recurringInvestments,
  handleWithdrawLater,
  showConfirmWithBalance,
  userData,
  liquidationData,
  isDowngradeSubscriptionFlow,
  contactSupportModalProps,
  showContactSupportModal,
}) => {
  const { height } = useWindowDimensions()
  return (
    <>
      {isWebView && (
        <FullscreenTakeover innerHeight={height}>
          <CloseAccountWrapper
            isWebView={isWebView}
            action={{
              onClick:
                showConfirmRequestScreen || showConfirmWithBalance
                  ? handleCancel
                  : handleDoneClick,
              shouldShowCloseIcon:
                !showConfirmRequestScreen && !showConfirmWithBalance,
            }}
            link={{
              onClick:
                showConfirmRequestScreen || showConfirmWithBalance
                  ? handleCancel
                  : null,
              text:
                showConfirmRequestScreen || showConfirmWithBalance
                  ? 'Cancel'
                  : null,
            }}
            handleCancel={
              !showConfirmRequestScreen && !showConfirmWithBalance
                ? handleDoneClick
                : handleCancel
            }
            actionStripTitle={
              isDowngradeSubscriptionFlow
                ? 'Change subscription'
                : 'Close account'
            }
            showCancellable={showConfirmRequestScreen || showConfirmWithBalance}
            shouldNotShowBack={
              !showConfirmRequestScreen && !showConfirmWithBalance
            }
          >
            {showConfirmRequestScreen && laterBalance !== undefined && (
              <RequestScreen
                laterBalance={laterBalance}
                isWebView={isWebView}
                handleCancel={handleCancel}
                handleCloseAccountSelection={handleCloseAccountSelection}
                modalActions={confirmationModalProps.modalActions}
                modalBody={confirmationModalProps.modalBody}
                modalHeader={confirmationModalProps.modalHeader}
                setShowCloseAccountModal={setShowCloseAccountModal}
                showCloseAccountModal={showCloseAccountModal}
                recurringInvestments={recurringInvestments}
                handleWithdrawLater={handleWithdrawLater}
              />
            )}
            {showLoadingScreen && (
              <LoadingScreen
                closureItems={closureItems}
                closureHeading={
                  laterBalance
                    ? 'We wish you the best'
                    : 'Requesting account closure'
                }
                isVisible={showLoadingScreen}
              />
            )}
            {showSuccessScreen && (
              <ConfirmationScreen
                handleDone={handleDoneClick}
                laterBalance={laterBalance}
                isWebView={isWebView}
              />
            )}
            {showConfirmWithBalance && (
              <WithdrawWithBalanceConfirmation
                laterBalance={laterBalance}
                userData={userData}
                modalActions={confirmationModalProps.modalActions}
                modalBody={confirmationModalProps.modalBody}
                modalHeader={confirmationModalProps.modalHeader}
                isWebView={isWebView}
                handleCancel={handleCancel}
                setShowCloseAccountModal={setShowCloseAccountModal}
                showCloseAccountModal={showCloseAccountModal}
                fullWithdrawItem={liquidationData.amount}
                recurringInvestments={recurringInvestments}
                handleCloseAccountSelection={handleCloseAccountSelection}
              />
            )}
          </CloseAccountWrapper>
        </FullscreenTakeover>
      )}

      {!isWebView && (
        <CloseAccountWrapper
          isWebView={isWebView}
          handleCancel={handleCancel}
          closeIcon={showConfirmRequestScreen ? false : true}
          showConfirmRequestScreen={showConfirmRequestScreen}
          showCancellable={showConfirmRequestScreen || showConfirmWithBalance}
        >
          {showConfirmRequestScreen && laterBalance !== undefined && (
            <RequestScreen
              laterBalance={laterBalance}
              isWebView={isWebView}
              handleCancel={handleCancel}
              handleCloseAccountSelection={handleCloseAccountSelection}
              modalActions={confirmationModalProps.modalActions}
              modalBody={confirmationModalProps.modalBody}
              modalHeader={confirmationModalProps.modalHeader}
              setShowCloseAccountModal={setShowCloseAccountModal}
              showCloseAccountModal={showCloseAccountModal}
              recurringInvestments={recurringInvestments}
              handleWithdrawLater={handleWithdrawLater}
            />
          )}
          {showLoadingScreen && (
            <LoadingScreen
              closureItems={closureItems}
              closureHeading={
                laterBalance
                  ? 'We wish you the best'
                  : 'Requesting account closure'
              }
              isVisible={showLoadingScreen}
              isWebView={isWebView}
            />
          )}
          {showSuccessScreen && (
            <ConfirmationScreen
              handleDone={handleDoneClick}
              laterBalance={laterBalance}
            />
          )}
          {showConfirmWithBalance && (
            <WithdrawWithBalanceConfirmation
              laterBalance={laterBalance}
              userData={userData}
              modalActions={confirmationModalProps.modalActions}
              modalBody={confirmationModalProps.modalBody}
              modalHeader={confirmationModalProps.modalHeader}
              isWebView={isWebView}
              handleCancel={handleCancel}
              setShowCloseAccountModal={setShowCloseAccountModal}
              showCloseAccountModal={showCloseAccountModal}
              fullWithdrawItem={liquidationData.amount}
              recurringInvestments={recurringInvestments}
              handleCloseAccountSelection={handleCloseAccountSelection}
            />
          )}
        </CloseAccountWrapper>
      )}

      {showContactSupportModal && (
        <ConfirmationModal
          message={contactSupportModalProps.modalBody}
          title={contactSupportModalProps.modalHeader}
          actions={contactSupportModalProps.modalActions}
        />
      )}
    </>
  )
}

export default enhance(Page)
export { enhance, Page }
